/**
 * ZenMarkup Privacy Analytics — Shared state module.
 *
 * Holds current date range, computed date boundaries, and cached API data.
 * No DOM access — pure state management.
 */

const DATE_RANGE_DEFAULT = '7d';

const state = {
	dateRange: DATE_RANGE_DEFAULT,
	startDate: '',
	endDate: '',
	cache: {}
};

/**
 * Compute start/end ISO date strings for a given range key.
 *
 * @param {string} range  One of 'today', '7d', '30d'.
 * @return {{ start: string, end: string }}
 */
function computeDates(range) {
	const now = new Date();
	const end = formatDate(now);
	let start;

	switch (range) {
		case 'today':
			start = end;
			break;
		case '30d':
			start = formatDate(daysAgo(now, 29));
			break;
		case '7d':
		default:
			start = formatDate(daysAgo(now, 6));
			break;
	}

	return { start: start, end: end };
}

/**
 * Return a new Date that is `n` days before `date`.
 *
 * @param {Date}   date
 * @param {number} n
 * @return {Date}
 */
function daysAgo(date, n) {
	const d = new Date(date);
	d.setDate(d.getDate() - n);
	return d;
}

/**
 * Format a Date as YYYY-MM-DD.
 *
 * @param {Date} date
 * @return {string}
 */
function formatDate(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return y + '-' + m + '-' + d;
}

/**
 * Initialize state with default date range.
 */
export function init() {
	setRange(DATE_RANGE_DEFAULT);
}

/**
 * Update the active date range and recompute boundaries.
 *
 * @param {string} range  One of 'today', '7d', '30d'.
 * @return {{ start: string, end: string }}
 */
export function setRange(range) {
	state.dateRange = range;
	const dates = computeDates(range);
	state.startDate = dates.start;
	state.endDate = dates.end;
	return dates;
}

/**
 * Get the current date range key.
 *
 * @return {string}
 */
export function getRange() {
	return state.dateRange;
}

/**
 * Get the current start date (YYYY-MM-DD).
 *
 * @return {string}
 */
export function getStartDate() {
	return state.startDate;
}

/**
 * Get the current end date (YYYY-MM-DD).
 *
 * @return {string}
 */
export function getEndDate() {
	return state.endDate;
}

/**
 * In-memory cache lifetime for stats responses. Long enough to make
 * tab switching (Overview/Traffic/Behavior/Geo) snappy, short enough
 * that new visits surface without a manual page reload or cache purge.
 */
const CACHE_TTL_MS = 30000;

/**
 * Retrieve cached API response for a cache key.
 * Entries past their TTL are evicted and treated as missing.
 *
 * @param {string} key
 * @return {*|null}
 */
export function getCache(key) {
	const entry = state.cache[key];
	if (!entry) {
		return null;
	}
	if (Date.now() - entry.t > CACHE_TTL_MS) {
		delete state.cache[key];
		return null;
	}
	return entry.data;
}

/**
 * Set a custom date range with explicit from/to dates.
 *
 * @param {string} from  Start date (YYYY-MM-DD).
 * @param {string} to    End date (YYYY-MM-DD).
 * @return {{ start: string, end: string }}
 */
export function setCustomDates(from, to) {
	state.dateRange = 'custom';
	state.startDate = from;
	state.endDate = to;
	return { start: from, end: to };
}

/**
 * Store an API response in the cache.
 *
 * @param {string} key
 * @param {*}      data
 */
export function setCache(key, data) {
	state.cache[key] = { data, t: Date.now() };
}
